
import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Home.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import BannerAds from "./BannerAds";
import InstallButton from "./InstallButton";
import { clearSession } from "../utils/session";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

function Home() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const welcomeShownRef = useRef(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const navigate = useNavigate();

  // ===== PARTICLES =====
  const particles = useMemo(() => {
    const particleCount = window.innerWidth < 768 ? 25 : 40;

    return Array.from({ length: particleCount }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      r: Math.random() * 1.5 + 0.5,
    }));
  }, []);

  // ===== TOAST =====
  const showToast = (message, type = "info") => {
    const oldToast = document.querySelector(".toast");

    if (oldToast) oldToast.remove();

    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;
    toast.innerText = message;

    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  };

  // ===== FETCH USER =====
  useEffect(() => {
    let mounted = true;

    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          if (mounted) {
            setUser(null);
            setCheckingAuth(false);
            navigate("/");
          }
          return;
        }

        const res = await axios.get(`${BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 8000,
        });

        if (!mounted) return;

        setUser(res.data);

        if (!welcomeShownRef.current) {
          if (res.data.isAdmin) {
            showToast(
              `Welcome back Admin, ${res.data.name}! 🚀`,
              "success"
            );
          } else {
            showToast(`Welcome ${res.data.name} 🚀`, "success");
          }

          welcomeShownRef.current = true;
        }
      } catch (err) {
        console.error("Fetch User Error:", err);

        if (
          err.response?.status === 401 ||
          err.response?.status === 403
        ) {
          clearSession();
          localStorage.removeItem("token");

          showToast(
            "Session expired. Please login again.",
            "error"
          );

          navigate("/");
        } else {
          showToast(
            "Failed to connect. Please refresh.",
            "error"
          );
        }

        if (mounted) setUser(null);
      } finally {
        if (mounted) setCheckingAuth(false);
      }
    };

    fetchUser();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  // ===== RESPONSIVE SCREEN WIDTH =====
  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", updateViewportWidth);

    return () => {
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, []);

  // ===== PARTICLE ANIMATION =====
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();

    let lastTime = 0;
    const fps = 24;
    const interval = 1000 / fps;

    const draw = (time) => {
      animationRef.current = requestAnimationFrame(draw);

      const delta = time - lastTime;
      if (delta < interval) return;

      lastTime = time - (delta % interval);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Connecting lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = dx * dx + dy * dy;

          if (dist < 6000) {
            ctx.beginPath();
            ctx.strokeStyle =
              `rgba(139,92,246,${0.15 - dist / 60000})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Moving dots
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(167,139,250,0.7)";
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });
    };

    animationRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", setCanvasSize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", setCanvasSize);
    };
  }, [particles]);

  // ===== MENU BUTTONS =====
  const buttons = [
    {
      to: "/add-blog",
      label: "Write Blog",
      icon: "✍️",
      color: "#7c3aed",
    },
    {
      to: "/blogs",
      label: "Read Blogs",
      icon: "📖",
      color: "#0891b2",
    },
    {
      to: "/pro",
      label: "My Profile",
      icon: "👤",
      color: "#059669",
    },
    {
      to: "/save",
      label: "Saved Blogs",
      icon: "🔖",
      color: "#d97706",
    },
    {
      to: "/upload-status",
      label: "Upload Status",
      icon: "📸",
      color: "#db2777",
    },
    {
      to: "/set",
      label: "Setting",
      icon: "⚙️",
      color: "#4f46e5",
    },
    {
      to: "/chat",
      label: "Chat",
      icon: "💬",
      color: "#0891b2",
    },
    {
      to: "/refer",
      label: "Refer & Earn",
      icon: "🎁",
      color: "#8b5cf6",
    },
    {
      to: "/support",
      label: "Buy Me a Tree",
      icon: "☕",
      color: "#8b5cf6",
    },
  ];

  // Show Sales/Admin options only when authorized
  const allButtons = [
    ...buttons,

    ...(user?.role === "sales_executive" &&
    user?.salesExecutive?.status !== "suspended"
      ? [
          {
            to: "/sales",
            label: "Sales Executive",
            icon: "📈",
            color: "#0d9488",
          },
        ]
      : []),

    ...(user?.isAdmin
      ? [
          {
            to: "/admin",
            label: "Admin Dashboard",
            icon: "🛠️",
            color: "#dc2626",
          },
        ]
      : []),
  ];

  // ===== LOADING SCREEN =====
  if (checkingAuth) {
    return (
      <div className="home-wrapper">
        <canvas ref={canvasRef} className="home-canvas" />

        <Navbar />

        <div className="home-container">
          <div className="loading-screen-full">
            <div className="loading-content">
              <div className="loading-spinner-large" />
              <p>Loading your space...</p>
              <small>Checking authentication...</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Circular radius changes based on screen width
  const radius =
    viewportWidth <= 360
      ? 82
      : viewportWidth <= 600
        ? 100
        : viewportWidth <= 1024
          ? 120
          : allButtons.length > 9
            ? 155
            : 130;

  return (
    <div className="home-wrapper">
      <canvas ref={canvasRef} className="home-canvas" />

      <Navbar />

      <div className="home-container">
        {/* HERO */}
        <div className="hero-section">
          <div className="hero-badge">
            🌐 Welcome to TheBlogsSphere – a Blogs sharing
            platform.
          </div>

          <h1 className="hero-title">
            Welcome back,
            <br />
            <span className="hero-name">{user.name}</span>
          </h1>

          <p className="hero-sub">
            Share knowledge. Learn together. Grow together.
          </p>

          <BannerAds />
        </div>

        {/* INFO GRID */}
        <div className="info-grid">
          <div className="info-card">
            <div className="info-icon">🌍</div>
            <h3>Public Platform</h3>
            <p>
              All users can view the blogs you publish publicly.
            </p>
          </div>

          <div className="info-card">
            <div className="info-icon">🔒</div>
            <h3>Secure Data</h3>
            <p>
              Your credentials and user data stay protected.
            </p>
          </div>

          <div className="info-card">
            <div className="info-icon">⚠️</div>
            <h3>Important Notice</h3>
            <p>
              Never upload private or sensitive information.
            </p>
          </div>

          <div className="info-card">
            <div className="info-icon">📱</div>
            <h3>Better Experience</h3>
            <p>
              Stable internet gives better speed and smoother
              loading.
            </p>
          </div>
        </div>

        {/* LEGAL NOTICE */}
        <div className="info-card legal-card">
          <div className="info-icon">⚖️</div>
          <h3>Legal & Safe Notice</h3>
          <p>
            Share only legal and safe content. Nudity, sexual,
            illegal, hateful, violent, spam, or misleading
            content is not allowed.
          </p>
        </div>

        {/* ABOUT */}
        <div className="note-box">
          <div className="note-header">
            <span>💡</span>
            <h2>About TheBlogsSphere</h2>
          </div>

          <p>
            TheBlogsSphere helps creators, developers, and
            learners share ideas, connect with the community,
            and grow together. Share your blogs, insights, and
            knowledge with people around the world.
          </p>

          <div className="note-tip">
            <strong>Pro Tip:</strong> Logout before leaving
            shared devices.
          </div>
        </div>

        <div className="home-footer-text">
          Made with ❤️ for creators and learners worldwide.
        </div>
      </div>

      {/* ===== CENTER CIRCULAR APP MENU ===== */}
      <div
        className={`floating-menu ${menuOpen ? "open" : ""}`}
      >
        {allButtons.map((btn, index) => {
          const angle =
            (index * 360) / allButtons.length - 90;

          const x =
            Math.cos((angle * Math.PI) / 180) * radius;

          const y =
            Math.sin((angle * Math.PI) / 180) * radius;

          return (
            <Link
              key={btn.to}
              to={btn.to}
              className="floating-menu-item"
              title={btn.label}
              aria-label={btn.label}
              style={{
                "--x": `${x}px`,
                "--y": `${y}px`,
                "--btn-color": btn.color,
                "--item-index": index,
              }}
              onClick={() => setMenuOpen(false)}
            >
              <span>{btn.icon}</span>
              <span className="floating-menu-tooltip">
                {btn.label}
              </span>
            </Link>
          );
        })}

        {/* MAIN CENTER ICON */}
        <button
          type="button"
          className="floating-menu-toggle"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "Close app menu" : "Open app menu"}
          aria-expanded={menuOpen}
        >

          {menuOpen ? (
  "✕"
) : (
  <img
    src="/blog.png"
    alt="App Logo"
    className="app-center-logo"
  />
)}
         
        </button>
      </div>

      <InstallButton />
      <Footer />
    </div>
  );
}

export default Home;
